import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  DnsCutoverStateDto,
  DnsEqualityReportDto,
  DnsImportExecuteResponse,
  DnsImportPlanDto,
  DnsImportResolutionChoice,
  DnsProviderDto,
  DnsProviderTestResultDto,
  DnsProviderType,
  DnsRecordType,
  DnsSettingsDto,
  DnsStatusDto,
  DnsTemplateDto,
  DnsTemplateSyncResultDto,
  DnsTestResultDto,
  DnsInventoryDto,
  PleskSnapshotDto,
} from '@cpm/shared';
import { DNS_PROVIDER_TYPES, DNS_RECORD_TYPES } from '@cpm/shared';
import { Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { DnsWorkspace } from '../components/dns-workspace.js';
import { HelpLink } from '../components/help.js';
import { useEffect, useState } from 'react';
import { api } from '../api/client.js';
import { useMe } from '../api/auth.js';
import { timeAgo } from '../components/charts.js';
import { PROVIDER_TYPE_LABELS } from '../components/dns-panels.js';
import { Modal } from '../components/Modal.js';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ErrorNote,
  Input,
  Label,
  PageHeader,
  Select,
  Spinner,
  Textarea,
  Tabs,
} from '../components/ui.js';

/**
 * Shared domain workspace with separate provider operations and selected settings.
 */

function splitLines(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

function ConnectionCard({ section }: { section: 'connection' | 'defaults' }) {
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: ['dns', 'settings'],
    queryFn: () => api.get<DnsSettingsDto>('/api/dns/settings'),
    retry: false,
  });
  const [apiUrl, setApiUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [nameservers, setNameservers] = useState('');
  const [defaultRecordIp, setDefaultRecordIp] = useState('');
  const [testResult, setTestResult] = useState<DnsTestResultDto | null>(null);

  useEffect(() => {
    if (settings.data) {
      setApiUrl(settings.data.apiUrl ?? '');
      setNameservers(settings.data.nameservers.join('\n'));
      setDefaultRecordIp(settings.data.defaultRecordIp ?? '');
    }
  }, [settings.data]);

  const save = useMutation({
    mutationFn: () =>
      api.patch(
        '/api/dns/settings',
        section === 'connection'
          ? {
              apiUrl,
              ...(apiKey !== '' ? { apiKey } : {}),
            }
          : {
              nameservers: splitLines(nameservers),
              defaultRecordIp: defaultRecordIp.trim() === '' ? null : defaultRecordIp.trim(),
            },
      ),
    onSuccess: () => {
      setApiKey('');
      void queryClient.invalidateQueries({ queryKey: ['dns'] });
    },
  });
  const test = useMutation({
    mutationFn: () =>
      api.post<DnsTestResultDto>('/api/dns/test', {
        ...(apiUrl !== '' ? { apiUrl } : {}),
        ...(apiKey !== '' ? { apiKey } : {}),
      }),
    onSuccess: (result) => setTestResult(result),
  });

  if (settings.isPending) {
    return (
      <Card>
        <CardHeader
          title={section === 'connection' ? 'PowerDNS connection' : 'Public nameservers & defaults'}
        />
        <Spinner />
      </Card>
    );
  }
  if (settings.error) return null; // not an admin

  return (
    <Card>
      <CardHeader
        title={section === 'connection' ? 'PowerDNS connection' : 'Public nameservers & defaults'}
        description={
          section === 'connection'
            ? "The authoritative server's API endpoint and key the panel uses (stored encrypted)."
            : 'Choose public nameservers and the default IPv4 address for zones.'
        }
        actions={
          settings.data.configured ? (
            <Badge tone="green">configured</Badge>
          ) : (
            <Badge tone="yellow">not configured</Badge>
          )
        }
      />
      <form
        className="space-y-4 px-5 py-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {section === 'connection' && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="dns-api-url">API URL</Label>
              <Input
                id="dns-api-url"
                required
                type="url"
                placeholder="http://powerdns:8081"
                value={apiUrl}
                onChange={(e) => setApiUrl(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="dns-api-key">
                {settings.data.hasApiKey ? 'API key (leave empty to keep)' : 'API key'}
              </Label>
              <Input
                id="dns-api-key"
                type="password"
                autoComplete="new-password"
                required={!settings.data.hasApiKey}
                placeholder={settings.data.hasApiKey ? 'unchanged' : undefined}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
              />
            </div>
          </div>
        )}
        {section === 'defaults' && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="dns-nameservers">Public nameservers (one per line)</Label>
              <Textarea
                id="dns-nameservers"
                rows={3}
                placeholder={'ns1.example.com\nns2.example.com'}
                value={nameservers}
                onChange={(e) => setNameservers(e.target.value)}
              />
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Written as apex NS records of every new zone — the servers actually serving it,
                never the hidden primary.
              </p>
            </div>
            <div>
              <Label htmlFor="dns-default-ip">Default record IP (optional)</Label>
              <Input
                id="dns-default-ip"
                placeholder="203.0.113.10"
                value={defaultRecordIp}
                onChange={(e) => setDefaultRecordIp(e.target.value)}
              />
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                IPv4 that <span className="font-mono">&lt;ip&gt;</span> expands to when a zone has
                no explicit IP.
              </p>
            </div>
          </div>
        )}
        {(save.error ?? test.error) != null && <ErrorNote error={save.error ?? test.error} />}
        {testResult && (
          <div
            className={
              testResult.ok
                ? 'rounded-lg bg-emerald-50 px-4 py-3 text-sm text-emerald-700 ring-1 ring-inset ring-emerald-600/10 dark:bg-emerald-500/10 dark:text-emerald-400'
                : 'rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 ring-1 ring-inset ring-red-600/10 dark:bg-red-500/10 dark:text-red-400'
            }
          >
            {testResult.ok
              ? `Connection OK — PowerDNS ${testResult.version ?? 'unknown version'}.`
              : `Connection failed: ${testResult.error}`}
          </div>
        )}
        <div className="flex justify-end gap-2">
          {section === 'connection' && (
            <Button
              type="button"
              variant="secondary"
              loading={test.isPending}
              onClick={() => test.mutate()}
            >
              Test connection
            </Button>
          )}
          <Button type="submit" loading={save.isPending}>
            Save
          </Button>
        </div>
      </form>
    </Card>
  );
}

// ── Plesk zone migration (V2-G2) ──────────────────────────────────

function CutoverControls({ zoneId, name }: { zoneId: string; name: string }) {
  const queryClient = useQueryClient();
  const [ttl, setTtl] = useState('300');
  const [equality, setEquality] = useState<DnsEqualityReportDto | null>(null);
  const state = useQuery({
    queryKey: ['dns', 'cutover', zoneId],
    queryFn: () => api.get<DnsCutoverStateDto>(`/api/dns/zones/${zoneId}/migration/cutover`),
    retry: false,
  });
  const refresh = () => {
    void queryClient.invalidateQueries({
      queryKey: ['dns', 'cutover', zoneId],
    });
    void queryClient.invalidateQueries({ queryKey: ['dns', 'status'] });
  };
  const check = useMutation({
    mutationFn: () =>
      api.post<DnsEqualityReportDto>(`/api/dns/zones/${zoneId}/migration/equality`, {}),
    onSuccess: setEquality,
  });
  const prepare = useMutation({
    mutationFn: () =>
      api.post<DnsCutoverStateDto>(`/api/dns/zones/${zoneId}/migration/prepare-cutover`, {
        ttl: Number(ttl),
        confirm: true,
      }),
    onSuccess: refresh,
  });
  const cutover = useMutation({
    mutationFn: () =>
      api.post<DnsCutoverStateDto>(`/api/dns/zones/${zoneId}/migration/cutover`, {
        confirm: true,
      }),
    onSuccess: refresh,
  });
  const rollback = useMutation({
    mutationFn: () =>
      api.post<DnsCutoverStateDto>(`/api/dns/zones/${zoneId}/migration/rollback`, {
        confirm: true,
      }),
    onSuccess: refresh,
  });
  const error = check.error ?? prepare.error ?? cutover.error ?? rollback.error;

  return (
    <div className="mt-3 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm">
          <span className="font-medium">{name}</span>{' '}
          {state.data && (
            <Badge tone={state.data.stage === 'cutover' ? 'green' : 'blue'}>
              {state.data.stage}
            </Badge>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" loading={check.isPending} onClick={() => check.mutate()}>
            Equality report
          </Button>
          <Input
            aria-label={`${name} cutover TTL`}
            className="w-24"
            type="number"
            min={60}
            max={86400}
            value={ttl}
            onChange={(event) => setTtl(event.target.value)}
          />
          <Button
            variant="secondary"
            loading={prepare.isPending}
            disabled={state.data?.stage !== 'imported'}
            onClick={() => {
              if (window.confirm(`Capture ${name} and lower customer-record TTLs to ${ttl}?`))
                prepare.mutate();
            }}
          >
            Prepare TTL
          </Button>
          <Button
            loading={cutover.isPending}
            disabled={!['ttl-lowered', 'ready'].includes(state.data?.stage ?? '')}
            onClick={() => {
              if (window.confirm(`Verify public authoritative answers and mark ${name} cut over?`))
                cutover.mutate();
            }}
          >
            Verify cutover
          </Button>
          <Button
            variant="secondary"
            loading={rollback.isPending}
            disabled={!state.data?.previous || state.data.stage === 'rolled-back'}
            onClick={() => {
              if (window.confirm(`Restore the exact captured destination state for ${name}?`))
                rollback.mutate();
            }}
          >
            Roll back
          </Button>
        </div>
      </div>
      {equality && (
        <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
          <Badge tone={equality.readyForCutover ? 'green' : 'red'}>
            {equality.readyForCutover ? 'equal / explained' : 'blocked'}
          </Badge>{' '}
          {equality.diffs.length} difference
          {equality.diffs.length === 1 ? '' : 's'} ·{' '}
          {equality.diffs.filter((diff) => diff.blocker).length} unexplained
          {equality.diffs.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer">Show differences</summary>
              <ul className="mt-1 space-y-1 font-mono">
                {equality.diffs.map((diff) => (
                  <li key={`${diff.side}:${diff.key}:${diff.kind}`}>
                    {diff.side} {diff.kind} {diff.key}: {diff.actual ?? 'missing'}
                    {diff.explanation ? ` — ${diff.explanation}` : ''}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
      {state.data?.readyAt && state.data.stage !== 'cutover' && (
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
          TTL wait ends {new Date(state.data.readyAt).toLocaleString()}.
        </p>
      )}
      {state.data?.previous && (
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
          Rollback captured {state.data.previous.panelRecords.length} panel records, delegation{' '}
          {state.data.previous.delegation.join(', ') || 'not observed'}, and{' '}
          {state.data.previous.dsRecords.length} DS record(s).
        </p>
      )}
      {error != null && <ErrorNote error={error} />}
    </div>
  );
}

function PleskZoneImportCard() {
  const queryClient = useQueryClient();
  const snapshots = useQuery({
    queryKey: ['plesk-import', 'snapshots'],
    queryFn: () => api.get<PleskSnapshotDto[]>('/api/plesk-import/snapshots'),
  });
  const [snapshotId, setSnapshotId] = useState('');
  const [acceptPartialWarnings, setAcceptPartialWarnings] = useState(false);
  const [resolutions, setResolutions] = useState<Record<string, DnsImportResolutionChoice>>({});
  const [importAsPrimary, setImportAsPrimary] = useState<Record<string, boolean>>({});
  const [plan, setPlan] = useState<DnsImportPlanDto | null>(null);
  const [executed, setExecuted] = useState<DnsImportExecuteResponse | null>(null);
  const resolutionList = () =>
    [...new Set([...Object.keys(resolutions), ...Object.keys(importAsPrimary)])].map(
      (sourceZoneId) => {
        const choice = resolutions[sourceZoneId];
        const zone = plan?.zones.find((candidate) => candidate.sourceZoneId === sourceZoneId);
        return {
          sourceZoneId,
          ...(choice ? { choice } : {}),
          importAsPrimary: importAsPrimary[sourceZoneId] ?? false,
          ...(choice === 'link-panel' && zone?.panelZoneId
            ? { panelZoneId: zone.panelZoneId }
            : {}),
        };
      },
    );
  const preview = useMutation({
    mutationFn: () =>
      api.post<{ plan: DnsImportPlanDto }>('/api/dns/plesk-import/dry-run', {
        snapshotId,
        resolutions: resolutionList(),
        acceptPartialWarnings,
      }),
    onSuccess: (response) => {
      setPlan(response.plan);
      setExecuted(null);
    },
  });
  const execute = useMutation({
    mutationFn: () =>
      api.post<DnsImportExecuteResponse>('/api/dns/plesk-import/execute', {
        snapshotId,
        resolutions: resolutionList(),
        acceptPartialWarnings,
      }),
    onSuccess: (response) => {
      setPlan(response.plan);
      setExecuted(response);
      void queryClient.invalidateQueries({ queryKey: ['dns'] });
    },
  });
  const candidates =
    snapshots.data?.filter((snapshot) => ['completed', 'partial'].includes(snapshot.status)) ?? [];

  return (
    <Card>
      <CardHeader
        title="Plesk DNS zone import"
        description="Dry-run a frozen inventory snapshot, resolve every collision, then import subscription-owned zones without writing to Plesk."
      />
      <div className="space-y-4 px-5 py-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-72 flex-1">
            <Label htmlFor="dns-import-snapshot">Frozen Plesk snapshot</Label>
            <Select
              id="dns-import-snapshot"
              value={snapshotId}
              onChange={(event) => {
                setSnapshotId(event.target.value);
                setPlan(null);
                setExecuted(null);
                setResolutions({});
                setImportAsPrimary({});
                setAcceptPartialWarnings(false);
              }}
            >
              <option value="">Select a snapshot</option>
              {candidates.map((snapshot) => (
                <option key={snapshot.id} value={snapshot.id}>
                  {new Date(snapshot.startedAt).toLocaleString()} · {snapshot.status} ·{' '}
                  {snapshot.stats?.counts.dns_zone ?? 0} DNS zones
                </option>
              ))}
            </Select>
          </div>
          {candidates.find((snapshot) => snapshot.id === snapshotId)?.status === 'partial' && (
            <label className="flex max-w-md items-center gap-2 text-xs text-amber-700 dark:text-amber-400">
              <input
                type="checkbox"
                checked={acceptPartialWarnings}
                onChange={(event) => setAcceptPartialWarnings(event.target.checked)}
              />
              I reviewed and accept every collection warning on this partial snapshot.
            </label>
          )}
          <Button
            variant="secondary"
            disabled={
              !snapshotId ||
              (candidates.find((snapshot) => snapshot.id === snapshotId)?.status === 'partial' &&
                !acceptPartialWarnings)
            }
            loading={preview.isPending}
            onClick={() => preview.mutate()}
          >
            Preview exact diff
          </Button>
          <Button
            disabled={!plan?.executable}
            loading={execute.isPending}
            onClick={() => {
              if (
                window.confirm('Execute this recomputed DNS import plan against the destination?')
              )
                execute.mutate();
            }}
          >
            Execute import
          </Button>
        </div>
        {(snapshots.error ?? preview.error ?? execute.error) != null && (
          <ErrorNote error={snapshots.error ?? preview.error ?? execute.error} />
        )}
        {plan && (
          <div className="space-y-3">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              <Badge tone={plan.executable ? 'green' : 'red'}>
                {plan.executable ? 'executable' : 'blocked'}
              </Badge>{' '}
              {plan.summary.create} create · {plan.summary.link} link · {plan.summary.conflict}{' '}
              conflict · {plan.summary.skip} skip · {plan.summary.recordsCreate} record creates ·{' '}
              {plan.summary.recordsUpdate} updates · {plan.summary.recordsHeld} preserved ·{' '}
              {plan.summary.providerOwned} provider-owned · {plan.summary.blockers} blockers
            </p>
            {plan.zones.map((zone) => (
              <div
                key={zone.sourceZoneId}
                className="rounded-lg border border-slate-200 p-3 dark:border-slate-700"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm">
                    <span className="font-medium">{zone.name}</span>{' '}
                    <Badge
                      tone={
                        zone.action === 'conflict'
                          ? 'red'
                          : zone.action === 'skip'
                            ? 'yellow'
                            : 'blue'
                      }
                    >
                      {zone.action}
                    </Badge>{' '}
                    <span className="text-xs text-slate-500">
                      {zone.source.enabled ? 'enabled' : 'disabled'} {zone.source.kind} ·{' '}
                      {zone.source.dnssec.enabled ? 'DNSSEC signed' : 'DNSSEC unsigned'}
                    </span>
                  </div>
                  {(zone.action === 'conflict' || zone.blockers.length > 0) && (
                    <div className="space-y-2">
                      <Select
                        aria-label={`${zone.name} zone decision`}
                        className="w-56"
                        value={resolutions[zone.sourceZoneId] ?? ''}
                        onChange={(event) =>
                          setResolutions((current) => ({
                            ...current,
                            [zone.sourceZoneId]: event.target.value as DnsImportResolutionChoice,
                          }))
                        }
                      >
                        <option value="">Choose zone decision</option>
                        {zone.panelZoneId && <option value="link-panel">Link panel zone</option>}
                        {!zone.panelZoneId && zone.powerdnsZoneId && (
                          <option value="claim-powerdns">Claim PowerDNS zone</option>
                        )}
                        <option value="skip">Skip zone</option>
                      </Select>
                      {(!zone.source.enabled || zone.source.kind === 'slave') &&
                        resolutions[zone.sourceZoneId] !== 'skip' && (
                          <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                            <input
                              type="checkbox"
                              checked={importAsPrimary[zone.sourceZoneId] ?? false}
                              onChange={(event) =>
                                setImportAsPrimary((current) => ({
                                  ...current,
                                  [zone.sourceZoneId]: event.target.checked,
                                }))
                              }
                            />
                            Import this disabled/secondary source as an enabled primary
                          </label>
                        )}
                    </div>
                  )}
                </div>
                {zone.blockers.length > 0 && (
                  <ul className="mt-2 list-disc pl-5 text-xs text-red-600 dark:text-red-400">
                    {zone.blockers.map((blocker) => (
                      <li key={blocker}>{blocker}</li>
                    ))}
                  </ul>
                )}
                <details className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                  <summary className="cursor-pointer">
                    {zone.recordDiffs.length} exact source-record decisions
                  </summary>
                  <ul className="mt-2 space-y-1 font-mono">
                    {zone.recordDiffs.map((diff) => (
                      <li key={diff.sourceRecordId}>
                        #{diff.sourceRecordId} {diff.action}: {diff.after?.name} {diff.after?.type}{' '}
                        {diff.after?.ttl} {diff.after?.content}
                        {diff.reason ? ` — ${diff.reason}` : ''}
                      </li>
                    ))}
                  </ul>
                </details>
              </div>
            ))}
            {(Object.keys(resolutions).length > 0 || Object.keys(importAsPrimary).length > 0) && (
              <Button
                variant="secondary"
                loading={preview.isPending}
                onClick={() => preview.mutate()}
              >
                Re-check with decisions
              </Button>
            )}
          </div>
        )}
        {executed && (
          <div>
            <p className="text-sm font-medium">Import run {executed.run.id} completed.</p>
            {executed.results
              .filter((result) => result.panelZoneId)
              .map((result) => (
                <CutoverControls
                  key={result.sourceZoneId}
                  zoneId={result.panelZoneId!}
                  name={result.name}
                />
              ))}
          </div>
        )}
      </div>
    </Card>
  );
}

// ── Zone templates ──────────────────────────────────────────────────

type TemplateRecordRow = {
  name: string;
  type: DnsRecordType;
  ttl: string;
  content: string;
};

function TemplateForm({ existing, onDone }: { existing?: DnsTemplateDto; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(existing?.name ?? '');
  const [isDefault, setIsDefault] = useState(existing?.isDefault ?? false);
  const [rows, setRows] = useState<TemplateRecordRow[]>(
    existing?.records.map((r) => ({
      name: r.name,
      type: r.type,
      ttl: r.ttl != null ? String(r.ttl) : '',
      content: r.content,
    })) ?? [{ name: '@', type: 'A', ttl: '', content: '<ip>' }],
  );

  const setRow = (index: number, patch: Partial<TemplateRecordRow>) =>
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const save = useMutation({
    mutationFn: () => {
      const records = rows.map((row) => ({
        name: row.name,
        type: row.type,
        ...(row.ttl.trim() !== '' ? { ttl: Number(row.ttl) } : {}),
        content: row.content,
      }));
      return existing
        ? api.patch(`/api/dns/templates/${existing.id}`, {
            name,
            isDefault,
            records,
          })
        : api.post('/api/dns/templates', { name, isDefault, records });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dns'] });
      onDone();
    },
  });

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div className="flex flex-wrap items-end gap-4">
        <div className="min-w-48 flex-1">
          <Label htmlFor="tpl-name">Name</Label>
          <Input id="tpl-name" required value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <label className="flex items-center gap-2 pb-2 text-sm">
          <input
            type="checkbox"
            className="size-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
          />
          Default for new zones
        </label>
      </div>
      <div>
        <Label>Records</Label>
        <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">
          <span className="font-mono">&lt;domain&gt;</span> and{' '}
          <span className="font-mono">&lt;ip&gt;</span> expand when the template is applied to a
          zone. Empty TTL uses the default (3600).
        </p>
        <div className="space-y-2">
          {rows.map((row, index) => (
            <div
              key={index}
              className="grid grid-cols-[1fr_6rem_5rem_1.5fr_auto] items-center gap-2"
            >
              <Input
                aria-label={`Record ${index + 1} name`}
                required
                placeholder="@"
                className="font-mono text-xs"
                value={row.name}
                onChange={(e) => setRow(index, { name: e.target.value })}
              />
              <Select
                aria-label={`Record ${index + 1} type`}
                value={row.type}
                onChange={(e) => setRow(index, { type: e.target.value as DnsRecordType })}
              >
                {DNS_RECORD_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Select>
              <Input
                aria-label={`Record ${index + 1} TTL`}
                type="number"
                min={60}
                max={604_800}
                placeholder="3600"
                value={row.ttl}
                onChange={(e) => setRow(index, { ttl: e.target.value })}
              />
              <Input
                aria-label={`Record ${index + 1} content`}
                required
                placeholder="<ip>"
                className="font-mono text-xs"
                value={row.content}
                onChange={(e) => setRow(index, { content: e.target.value })}
              />
              <Button
                type="button"
                variant="ghost"
                className="hover:!text-red-600"
                aria-label={`Remove record ${index + 1}`}
                onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          ))}
        </div>
        <Button
          type="button"
          variant="ghost"
          className="mt-2"
          onClick={() =>
            setRows((prev) => [...prev, { name: '@', type: 'A', ttl: '', content: '' }])
          }
        >
          <Plus className="size-4" /> Add record
        </Button>
      </div>
      {save.error != null && <ErrorNote error={save.error} />}
      <div className="flex justify-end">
        <Button type="submit" loading={save.isPending}>
          {existing ? 'Save changes' : 'Create template'}
        </Button>
      </div>
    </form>
  );
}

function TemplatesCard({ isAdmin }: { isAdmin: boolean }) {
  const queryClient = useQueryClient();
  const templates = useQuery({
    queryKey: ['dns', 'templates'],
    queryFn: () => api.get<DnsTemplateDto[]>('/api/dns/templates'),
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<DnsTemplateDto | null>(null);
  const [applyResults, setApplyResults] = useState<Record<string, DnsTemplateSyncResultDto[]>>({});

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['dns'] });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/dns/templates/${id}`),
    onSuccess: invalidate,
  });
  const apply = useMutation({
    mutationFn: (id: string) =>
      api.post<DnsTemplateSyncResultDto[]>(`/api/dns/templates/${id}/apply`),
    onSuccess: (results, id) => {
      setApplyResults((prev) => ({ ...prev, [id]: results }));
      invalidate();
    },
  });

  if (templates.isPending) {
    return (
      <Card>
        <CardHeader title="Zone templates" />
        <Spinner />
      </Card>
    );
  }
  if (templates.error) return <ErrorNote error={templates.error} />;

  return (
    <Card>
      <CardHeader
        title="Zone templates"
        description="Record sets seeded into new zones; re-apply pushes template changes to existing zones."
        actions={
          isAdmin ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" /> New template
            </Button>
          ) : undefined
        }
      />
      {(remove.error ?? apply.error) != null && (
        <div className="px-5 pt-3">
          <ErrorNote error={remove.error ?? apply.error} />
        </div>
      )}
      {templates.data.length === 0 ? (
        <p className="px-5 py-4 text-sm text-slate-500 dark:text-slate-400">No templates yet.</p>
      ) : (
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {templates.data.map((template) => {
            const results = applyResults[template.id];
            return (
              <li key={template.id} className="px-5 py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-medium">{template.name}</span>
                    {template.isDefault && <Badge tone="blue">default</Badge>}
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      {template.records.length} record
                      {template.records.length === 1 ? '' : 's'} · {template.zoneCount} zone
                      {template.zoneCount === 1 ? '' : 's'}
                    </span>
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        loading={apply.isPending && apply.variables === template.id}
                        onClick={() => {
                          if (
                            window.confirm(
                              `Re-apply "${template.name}" to its ${template.zoneCount} zone(s)? User-modified records are preserved.`,
                            )
                          ) {
                            apply.mutate(template.id);
                          }
                        }}
                      >
                        <RefreshCw className="size-4" /> Apply to zones
                      </Button>
                      <Button
                        variant="ghost"
                        aria-label={`Edit template ${template.name}`}
                        onClick={() => setEditing(template)}
                      >
                        <Pencil className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        className="hover:!text-red-600"
                        aria-label={`Delete template ${template.name}`}
                        onClick={() => {
                          if (
                            window.confirm(
                              `Delete template ${template.name}? Zones using it keep their records.`,
                            )
                          ) {
                            remove.mutate(template.id);
                          }
                        }}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  )}
                </div>
                {results && (
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Applied to {results.length} zone
                    {results.length === 1 ? '' : 's'}: {results.reduce((n, r) => n + r.added, 0)}{' '}
                    added · {results.reduce((n, r) => n + r.updated, 0)} updated ·{' '}
                    {results.reduce((n, r) => n + r.removed, 0)} removed ·{' '}
                    {results.reduce((n, r) => n + r.preserved, 0)} preserved (user-modified)
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <Modal title="New zone template" open={createOpen} onClose={() => setCreateOpen(false)} wide>
        <TemplateForm onDone={() => setCreateOpen(false)} />
      </Modal>
      <Modal
        title={editing ? `Edit ${editing.name}` : 'Edit template'}
        open={editing !== null}
        onClose={() => setEditing(null)}
        wide
      >
        {editing && <TemplateForm existing={editing} onDone={() => setEditing(null)} />}
      </Modal>
    </Card>
  );
}

// ── Providers ───────────────────────────────────────────────────────

const CREDENTIALS_LABELS: Record<DnsProviderType, { label: string; hint?: string }> = {
  cloudflare: { label: 'API token', hint: 'Token with Zone:Edit permission.' },
  route53: { label: 'Access key', hint: 'Format: accessKeyId:secretAccessKey' },
  azure: { label: 'Client secret' },
  digitalocean: { label: 'API token' },
  axfr: {
    label: 'TSIG key secret (optional)',
    hint: 'The key name goes in the field above.',
  },
};

function ProviderForm({ existing, onDone }: { existing?: DnsProviderDto; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [type, setType] = useState<DnsProviderType>(existing?.type ?? 'cloudflare');
  const [label, setLabel] = useState(existing?.label ?? '');
  const [credentials, setCredentials] = useState('');
  const config = existing?.config;
  const [accountId, setAccountId] = useState(config?.accountId ?? '');
  const [tenantId, setTenantId] = useState(config?.tenantId ?? '');
  const [clientId, setClientId] = useState(config?.clientId ?? '');
  const [subscriptionId, setSubscriptionId] = useState(config?.subscriptionId ?? '');
  const [resourceGroup, setResourceGroup] = useState(config?.resourceGroup ?? '');
  const [allowAxfrFrom, setAllowAxfrFrom] = useState((config?.allowAxfrFrom ?? []).join('\n'));
  const [notifyTargets, setNotifyTargets] = useState((config?.notifyTargets ?? []).join('\n'));
  const [checkServers, setCheckServers] = useState((config?.checkServers ?? []).join('\n'));
  const [tsigKeyName, setTsigKeyName] = useState(config?.tsigKeyName ?? '');

  const save = useMutation({
    mutationFn: () => {
      const nextConfig =
        type === 'cloudflare'
          ? {
              ...(accountId.trim() !== '' ? { accountId: accountId.trim() } : {}),
            }
          : type === 'azure'
            ? { tenantId, clientId, subscriptionId, resourceGroup }
            : type === 'axfr'
              ? {
                  allowAxfrFrom: splitLines(allowAxfrFrom),
                  notifyTargets: splitLines(notifyTargets),
                  checkServers: splitLines(checkServers),
                  ...(tsigKeyName.trim() !== '' ? { tsigKeyName: tsigKeyName.trim() } : {}),
                }
              : {};
      const body = {
        label,
        config: nextConfig,
        ...(credentials !== '' ? { credentials } : {}),
      };
      return existing
        ? api.patch(`/api/dns/providers/${existing.id}`, body)
        : api.post('/api/dns/providers', { type, ...body });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dns'] });
      onDone();
    },
  });
  const creds = CREDENTIALS_LABELS[type];

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="prov-type">Type</Label>
          <Select
            id="prov-type"
            disabled={existing !== undefined}
            value={type}
            onChange={(e) => setType(e.target.value as DnsProviderType)}
          >
            {DNS_PROVIDER_TYPES.map((t) => (
              <option key={t} value={t}>
                {PROVIDER_TYPE_LABELS[t]}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <Label htmlFor="prov-label">Label</Label>
          <Input
            id="prov-label"
            required
            placeholder="Cloudflare (main account)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </div>
      </div>
      {type === 'cloudflare' && (
        <div>
          <Label htmlFor="prov-account">Account ID (optional)</Label>
          <Input
            id="prov-account"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
          />
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Needed only so the panel can create zones that don't exist there yet.
          </p>
        </div>
      )}
      {type === 'azure' && (
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="prov-tenant">Tenant ID</Label>
            <Input
              id="prov-tenant"
              required
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="prov-client">Client ID</Label>
            <Input
              id="prov-client"
              required
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="prov-subscription">Subscription ID</Label>
            <Input
              id="prov-subscription"
              required
              value={subscriptionId}
              onChange={(e) => setSubscriptionId(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="prov-rg">Resource group</Label>
            <Input
              id="prov-rg"
              required
              value={resourceGroup}
              onChange={(e) => setResourceGroup(e.target.value)}
            />
          </div>
        </div>
      )}
      {type === 'axfr' && (
        <>
          <div>
            <Label htmlFor="prov-axfr-allow">Allow AXFR from (one IP per line)</Label>
            <Textarea
              id="prov-axfr-allow"
              rows={2}
              placeholder="198.51.100.53"
              value={allowAxfrFrom}
              onChange={(e) => setAllowAxfrFrom(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="prov-axfr-notify">NOTIFY targets (IP or IP:port, one per line)</Label>
            <Textarea
              id="prov-axfr-notify"
              rows={2}
              placeholder="198.51.100.53:5300"
              value={notifyTargets}
              onChange={(e) => setNotifyTargets(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="prov-axfr-check">Drift-check servers (hostnames, one per line)</Label>
            <Textarea
              id="prov-axfr-check"
              rows={2}
              placeholder="ns1.example.com"
              value={checkServers}
              onChange={(e) => setCheckServers(e.target.value)}
            />
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Their SOA serial is compared with the primary's to detect lagging secondaries.
            </p>
          </div>
          <div>
            <Label htmlFor="prov-tsig">TSIG key name (optional)</Label>
            <Input
              id="prov-tsig"
              placeholder="axfr-key"
              value={tsigKeyName}
              onChange={(e) => setTsigKeyName(e.target.value)}
            />
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              A key already created on the PowerDNS server; signs outgoing transfers.
            </p>
          </div>
        </>
      )}
      <div>
        <Label htmlFor="prov-credentials">
          {existing?.hasCredentials ? `${creds.label} (leave empty to keep)` : creds.label}
        </Label>
        <Input
          id="prov-credentials"
          type="password"
          autoComplete="new-password"
          required={!existing && type !== 'axfr'}
          placeholder={existing?.hasCredentials ? 'unchanged' : undefined}
          value={credentials}
          onChange={(e) => setCredentials(e.target.value)}
        />
        {creds.hint && (
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{creds.hint}</p>
        )}
      </div>
      {save.error != null && <ErrorNote error={save.error} />}
      <div className="flex justify-end">
        <Button type="submit" loading={save.isPending}>
          {existing ? 'Save changes' : 'Add provider'}
        </Button>
      </div>
    </form>
  );
}

function ProvidersCard({ isAdmin }: { isAdmin: boolean }) {
  const queryClient = useQueryClient();
  const providers = useQuery({
    queryKey: ['dns', 'providers'],
    queryFn: () => api.get<DnsProviderDto[]>('/api/dns/providers'),
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<DnsProviderDto | null>(null);
  const [testResults, setTestResults] = useState<Record<string, DnsProviderTestResultDto>>({});

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/dns/providers/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['dns'] }),
  });
  const test = useMutation({
    mutationFn: (id: string) => api.post<DnsProviderTestResultDto>(`/api/dns/providers/${id}/test`),
    onSuccess: (result, id) => setTestResults((prev) => ({ ...prev, [id]: result })),
  });

  if (providers.isPending) {
    return (
      <Card>
        <CardHeader title="Sync providers" />
        <Spinner />
      </Card>
    );
  }
  if (providers.error) return <ErrorNote error={providers.error} />;

  return (
    <Card>
      <CardHeader
        title="Sync providers"
        description="Where zones replicate to for public serving — API push or AXFR secondaries."
        actions={
          isAdmin ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" /> Add provider
            </Button>
          ) : undefined
        }
      />
      {remove.error != null && (
        <div className="px-5 pt-3">
          <ErrorNote error={remove.error} />
        </div>
      )}
      {providers.data.length === 0 ? (
        <p className="px-5 py-4 text-sm text-slate-500 dark:text-slate-400">
          No providers yet — zones stay on the hidden primary only, which no resolver can reach.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {providers.data.map((provider) => {
            const result = testResults[provider.id];
            return (
              <li key={provider.id} className="px-5 py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-medium">{provider.label}</span>
                    <Badge tone="blue">{PROVIDER_TYPE_LABELS[provider.type]}</Badge>
                    {provider.hasCredentials ? (
                      <Badge tone="green">credentials set</Badge>
                    ) : provider.type !== 'axfr' ? (
                      <Badge tone="yellow">no credentials</Badge>
                    ) : null}
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      {provider.zoneCount} zone
                      {provider.zoneCount === 1 ? '' : 's'}
                    </span>
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        loading={test.isPending && test.variables === provider.id}
                        onClick={() => test.mutate(provider.id)}
                      >
                        Test
                      </Button>
                      <Button
                        variant="ghost"
                        aria-label={`Edit provider ${provider.label}`}
                        onClick={() => setEditing(provider)}
                      >
                        <Pencil className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        className="hover:!text-red-600"
                        aria-label={`Delete provider ${provider.label}`}
                        onClick={() => {
                          if (
                            window.confirm(
                              `Delete provider ${provider.label}? Zone copies at the provider are left in place.`,
                            )
                          ) {
                            remove.mutate(provider.id);
                          }
                        }}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  )}
                </div>
                {result && (
                  <p
                    className={
                      result.ok
                        ? 'mt-1 text-xs text-emerald-600 dark:text-emerald-400'
                        : 'mt-1 text-xs text-red-600 dark:text-red-400'
                    }
                  >
                    {result.ok ? 'Connection OK.' : `Test failed: ${result.error}`}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <Modal title="Add sync provider" open={createOpen} onClose={() => setCreateOpen(false)}>
        <ProviderForm onDone={() => setCreateOpen(false)} />
      </Modal>
      <Modal
        title={editing ? `Edit ${editing.label}` : 'Edit provider'}
        open={editing !== null}
        onClose={() => setEditing(null)}
      >
        {editing && <ProviderForm existing={editing} onDone={() => setEditing(null)} />}
      </Modal>
    </Card>
  );
}

// ── Reconciliation ──────────────────────────────────────────────────

function ReconcileCard({ status }: { status: DnsStatusDto }) {
  const queryClient = useQueryClient();
  const reconcile = useMutation({
    mutationFn: () => api.post('/api/dns/reconcile'),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['dns'] }),
  });
  const run = status.lastReconcile;

  return (
    <Card>
      <CardHeader
        title="Reconciliation"
        description="Compares the panel's zones and records with the PowerDNS server, both directions."
        actions={
          <Button
            variant="secondary"
            loading={reconcile.isPending || status.running}
            disabled={!status.configured}
            onClick={() => reconcile.mutate()}
          >
            <RefreshCw className="size-4" /> Reconcile now
          </Button>
        }
      />
      <div className="px-5 py-4 text-sm">
        <p className="mb-3 text-slate-500 dark:text-slate-400">
          {status.configured ? (
            <Badge tone="green">configured</Badge>
          ) : (
            <Badge tone="yellow">not configured</Badge>
          )}{' '}
          <span className="ml-1">
            {status.zoneCount} zone{status.zoneCount === 1 ? '' : 's'} · {status.recordCount} record
            {status.recordCount === 1 ? '' : 's'} · {status.providerCount} provider
            {status.providerCount === 1 ? '' : 's'}
          </span>
        </p>
        {reconcile.error != null && (
          <div className="mb-3">
            <ErrorNote error={reconcile.error} />
          </div>
        )}
        {!run ? (
          <p className="text-slate-500 dark:text-slate-400">No reconcile has run yet.</p>
        ) : (
          <div className="space-y-2">
            <p className="flex flex-wrap items-center gap-2">
              <Badge tone={run.status === 'success' ? 'green' : 'red'}>{run.status}</Badge>
              <span className="text-slate-500 dark:text-slate-400">
                {timeAgo(run.startedAt)}
                {run.durationMs !== null && ` · ${run.durationMs} ms`}
              </span>
            </p>
            {run.error && <ErrorNote error={new Error(run.error)} />}
            {run.stats && (
              <>
                <p className="text-slate-500 dark:text-slate-400">
                  {run.stats.zonesChecked} zones checked · {run.stats.zonesCreated} created ·{' '}
                  {run.stats.zonesRelinked} relinked · {run.stats.zonesOrphaned} orphaned ·{' '}
                  {run.stats.rrsetsRepaired} RRsets repaired · {run.stats.autoRecordsAdded}{' '}
                  auto-records added · {run.stats.autoRecordsRemoved} removed ·{' '}
                  {run.stats.autoRecordsBlocked} blocked · {run.stats.dsRefreshed} DS refreshed
                </p>
                {run.stats.unmanagedZones.length > 0 && (
                  <p className="rounded-lg bg-amber-50 px-4 py-3 text-amber-700 ring-1 ring-inset ring-amber-600/10 dark:bg-amber-500/10 dark:text-amber-400">
                    Unmanaged zones on the DNS server (not claimed by any subscription — the panel
                    never deletes them):{' '}
                    <span className="font-mono">{run.stats.unmanagedZones.join(', ')}</span>
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

function PublishingRecoveryCard() {
  const [page, setPage] = useState(1);
  const domains = useQuery({
    queryKey: ['dns', 'operations-domains', page],
    queryFn: () => api.get<DnsInventoryDto>(`/api/dns/domains?page=${page}`),
    refetchInterval: 30_000,
  });
  const attention =
    domains.data?.items.filter(
      (domain) =>
        !domain.zoneId ||
        domain.primary.state !== 'synchronized' ||
        domain.publication.state !== 'synchronized',
    ) ?? [];
  return (
    <Card>
      <CardHeader
        title="Publishing & recovery"
        description="Review setup, drift and unverified publication across assigned domains."
      />
      <div className="space-y-4 px-5 pb-4">
        {domains.isPending ? (
          <Spinner />
        ) : domains.error ? (
          <ErrorNote error={domains.error} />
        ) : (
          <>
            {attention.map((domain) => (
              <div
                key={domain.id}
                className="flex flex-wrap items-center justify-between gap-3 border-b pb-3"
              >
                <div className="space-y-1">
                  <p className="break-all font-semibold">{domain.name}</p>
                  <p className="text-sm text-slate-500">
                    {domain.subscriptionName}
                    {domain.customerName ? ` · ${domain.customerName}` : ''}
                  </p>
                  <p className="text-sm">
                    Primary: {domain.primary.state} · Public:{' '}
                    {domain.publication.state === 'unconfigured'
                      ? 'awaiting provider setup'
                      : domain.publication.state}
                  </p>
                  <p className="text-xs text-slate-500">
                    Primary{' '}
                    {domain.primary.checkedAt
                      ? `checked ${timeAgo(domain.primary.checkedAt)}`
                      : 'not verified'}{' '}
                    · Public{' '}
                    {domain.publication.checkedAt
                      ? `checked ${timeAgo(domain.publication.checkedAt)}`
                      : 'not verified'}
                  </p>
                </div>
                <Link
                  className="text-sm text-blue-600 underline"
                  to={
                    domain.zoneId
                      ? `/dns?zoneId=${domain.zoneId}&domainTab=${domain.primary.state === 'orphaned' ? 'settings' : 'setup'}`
                      : `/dns?search=${encodeURIComponent(domain.name)}`
                  }
                >
                  {domain.zoneId ? 'Inspect domain' : 'Open assigned domain'}
                </Link>
              </div>
            ))}
            {!attention.length && <p>No domains on this page need attention.</p>}
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                Previous
              </Button>
              <p className="text-sm">
                Page {page} of assigned domains · {domains.data.total} total
              </p>
              <Button
                variant="secondary"
                disabled={page * domains.data.pageSize >= domains.data.total}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

export function DnsPage({ portal = false }: { portal?: boolean }) {
  const me = useMe();
  const [params, setParams] = useSearchParams();
  const isProvider = !portal && (me.data?.role === 'admin' || me.data?.role === 'staff');
  const isAdmin = me.data?.role === 'admin';
  const view = isProvider ? (params.get('view') ?? 'domains') : 'domains';
  const section = params.get('section') ?? 'connection';
  const status = useQuery({
    queryKey: ['dns', 'status'],
    queryFn: () => api.get<DnsStatusDto>('/api/dns/status'),
    enabled: isProvider && view === 'operations',
    refetchInterval: 60_000,
  });
  const change = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    next.set(key, value);
    setParams(next);
  };
  return (
    <div className="space-y-4">
      <PageHeader
        title="DNS"
        description="Manage domains and DNS records."
        actions={
          <HelpLink
            topic={portal ? 'dns/customer-records' : 'dns/overview'}
            shell={portal ? 'portal' : 'panel'}
          >
            DNS help
          </HelpLink>
        }
      />
      {isProvider && (
        <Tabs
          active={view}
          onChange={(value) => change('view', value)}
          tabs={[
            { key: 'domains', label: 'Domains' },
            { key: 'operations', label: 'Operations' },
            { key: 'settings', label: 'Settings' },
          ]}
        />
      )}
      {view === 'domains' && <DnsWorkspace portal={portal} />}
      {view === 'operations' && (
        <>
          <PublishingRecoveryCard />
          {status.isPending ? (
            <Spinner />
          ) : status.error ? (
            <ErrorNote error={status.error} />
          ) : (
            <ReconcileCard status={status.data} />
          )}
          {isAdmin && <PleskZoneImportCard />}
        </>
      )}
      {view === 'settings' && (
        <>
          <Label htmlFor="dns-settings-section">Settings section</Label>
          <Select
            id="dns-settings-section"
            value={section}
            onChange={(e) => change('section', e.target.value)}
          >
            <option value="connection">Connection</option>
            <option value="defaults">Public nameservers & defaults</option>
            <option value="templates">Templates</option>
            <option value="providers">External providers</option>
          </Select>
          {isAdmin && (section === 'connection' || section === 'defaults') && (
            <ConnectionCard key={section} section={section} />
          )}
          {!isAdmin && ['connection', 'defaults'].includes(section) && (
            <p>Only an administrator can change shared DNS settings.</p>
          )}
          {section === 'templates' && <TemplatesCard isAdmin={isAdmin} />}
          {section === 'providers' && <ProvidersCard isAdmin={isAdmin} />}
        </>
      )}
    </div>
  );
}
